// Unread pull request activity (HRTH-115): which pull requests have events this device
// hasn't seen, per repository, and notifications for new ones. The server says only that
// something happened (a `pull` frame, and counts from GET /pulls); what happened is
// decrypted here. Seen counts stay on this device.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { PullActivity } from '../api/types'
import { displayName } from '../lib/format'
import { decryptPullBody, describeEvent, unreadPulls } from '../lib/pulls'
import { useSession } from './session'
import { useUnread } from './unread'

const KEY = 'pullsSeen'

export const usePulls = defineStore('pulls', () => {
  const activity = ref<PullActivity[]>([])
  /** Events counted per pull request when this device last showed it; null until loaded. */
  const seen = ref<Record<string, number> | null>(null)
  /** Bumped per repository when one of its pull requests changes, so open views reload. */
  const changed = ref<Record<string, number>>({})
  /** The pull request on screen: it's read while visible. */
  const viewing = ref<string | null>(null)

  const unread = computed(() => (seen.value ? unreadPulls(activity.value, seen.value, useSession().me) : []))
  const byLibrary = computed(() => {
    const out: Record<string, number> = {}
    for (const p of unread.value) out[p.library] = (out[p.library] ?? 0) + 1
    return out
  })
  const total = computed(() => unread.value.length)
  const isUnread = (pull: string) => unread.value.some((p) => p.id === pull)

  function persist(): void {
    void useSession().storageHandle()?.setKv(KEY, { ...seen.value })
  }

  async function refresh(): Promise<void> {
    const session = useSession()
    try {
      activity.value = (await session.api.pullActivity()).pulls
    } catch {
      return // Not signed in yet, or offline: the next poke or catch-up tries again.
    }
    if (seen.value === null) {
      const stored = await session.storageHandle()?.getKv<Record<string, number>>(KEY)
      // A device starts with everything that came before it read.
      seen.value = stored ?? Object.fromEntries(activity.value.map((p) => [p.id, p.events]))
      if (!stored) persist()
    }
    if (viewing.value && !document.hidden) {
      const p = activity.value.find((x) => x.id === viewing.value)
      if (p) markSeen(p.id, p.events)
    }
  }

  function markSeen(pull: string, events: number): void {
    if (!seen.value || (seen.value[pull] ?? 0) >= events) return
    seen.value = { ...seen.value, [pull]: events }
    persist()
  }

  function setViewing(pull: string | null, events?: number): void {
    viewing.value = pull
    if (pull && events !== undefined && !document.hidden) markSeen(pull, events)
  }

  /** A `pull` frame: refresh, tell views, and notify about someone else's new event. */
  async function onFrame(library: string, pull: string): Promise<void> {
    const before = activity.value.find((p) => p.id === pull)?.events ?? 0
    changed.value = { ...changed.value, [library]: (changed.value[library] ?? 0) + 1 }
    await refresh()
    const p = activity.value.find((x) => x.id === pull)
    if (!p || p.events <= before || p.last_author === useSession().me) return
    if (viewing.value === pull && !document.hidden) return
    await notify(p).catch(() => undefined)
  }

  async function notify(p: PullActivity): Promise<void> {
    const session = useSession()
    if (!useUnread().notifications || typeof Notification === 'undefined') return
    // Listing libraries unwraps keys this device hasn't opened yet (a newly shared repository).
    const repo = (await session.libraries()).find((l) => l.id === p.library)
    const key = session.libraryKey(p.library)
    if (!repo || !key) return
    const detail = await session.api.pull(p.id)
    const opened = detail.events[0] && (await decryptPullBody(key, p.library, detail.events[0]))
    const last = detail.events.at(-1)
    const body = last && (await decryptPullBody(key, p.library, last))
    if (!body || !last) return
    const title = opened?.type === 'opened' ? opened.title : `#${p.number}`
    const who = last.author ? displayName(session.profiles, last.author) : 'Someone'
    new Notification(`${who} · ${repo.name} #${p.number}`, { body: describeEvent(body, title), tag: p.id })
  }

  return { activity, seen, changed, unread, byLibrary, total, isUnread, refresh, markSeen, setViewing, onFrame }
})
